import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogScrollArea,
  DialogFooter,
} from '../../ui/dialog';
import {
  Bookmark,
  Loader2,
  RefreshCw,
  Trash2,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  xBookmarksService,
  XBookmarkSyncInterval,
  XBookmarksStatus,
} from '../../../services/xBookmarksService';

interface XBookmarksSuccessModalContentProps {
  bindingId: string;
  status: XBookmarksStatus | null;
  onRefresh: () => void;
  onClose: () => void;
}

const SYNC_INTERVAL_LABEL_KEYS: Record<XBookmarkSyncInterval, string> = {
  5: 'xBookmarksIntegration.syncInterval.options.5',
  15: 'xBookmarksIntegration.syncInterval.options.15',
  60: 'xBookmarksIntegration.syncInterval.options.60',
};

const BACKFILL_LABEL_KEYS: Record<
  NonNullable<XBookmarksStatus['backfill_status']>,
  string
> = {
  pending: 'xBookmarksIntegration.backfill.pending',
  in_progress: 'xBookmarksIntegration.backfill.in_progress',
  complete: 'xBookmarksIntegration.backfill.complete',
  failed: 'xBookmarksIntegration.backfill.failed',
  limit_reached: 'xBookmarksIntegration.backfill.limit_reached',
};

const SYNC_INTERVALS: XBookmarkSyncInterval[] = [5, 15, 60];

function backfillBadgeVariant(
  status: XBookmarksStatus['backfill_status']
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'complete') return 'default';
  if (status === 'failed' || status === 'limit_reached') return 'destructive';
  if (status === 'in_progress') return 'secondary';
  return 'outline';
}

export const XBookmarksSuccessModalContent: React.FC<
  XBookmarksSuccessModalContentProps
> = ({ bindingId, status, onRefresh, onClose }) => {
  const { t } = useTranslation('integrations');
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [syncInterval, setSyncInterval] = useState<XBookmarkSyncInterval>(
    status?.sync_interval_minutes ?? 15
  );

  useEffect(() => {
    if (status?.sync_interval_minutes) {
      setSyncInterval(status.sync_interval_minutes);
    }
  }, [status?.sync_interval_minutes]);

  const handleSyncNow = async () => {
    setIsSyncing(true);
    try {
      const result = await xBookmarksService.syncNow(bindingId);
      if (result.success) {
        toast.success(t('xBookmarksIntegration.messages.syncStarted'));
      } else {
        toast.error(result.error || t('xBookmarksIntegration.messages.syncFailed'));
      }
      onRefresh();
    } catch {
      toast.error(t('xBookmarksIntegration.messages.syncFailed'));
    } finally {
      setIsSyncing(false);
    }
  };

  const handleIntervalChange = async (minutes: XBookmarkSyncInterval) => {
    setSyncInterval(minutes);
    try {
      await xBookmarksService.updateSettings(bindingId, minutes);
      toast.success(t('xBookmarksIntegration.messages.settingsSaved'));
      onRefresh();
    } catch {
      toast.error(t('xBookmarksIntegration.messages.settingsFailed'));
    }
  };

  const handleDisconnect = async () => {
    const confirmed = window.confirm(t('xBookmarksIntegration.disconnect.confirm'));
    if (!confirmed) return;
    setIsDisconnecting(true);
    try {
      await xBookmarksService.disconnect(bindingId);
      toast.success(t('xBookmarksIntegration.messages.disconnected'));
      onClose();
    } catch {
      toast.error(t('xBookmarksIntegration.messages.disconnectFailed'));
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
        <DialogTitle className="flex items-start gap-3 text-xl">
          <CheckCircle2 className="w-8 h-8 text-green-500 shrink-0" aria-hidden />
          {t('xBookmarksIntegration.success.title')}
        </DialogTitle>
        <DialogDescription>
          {status?.x_username
            ? t('xBookmarksIntegration.success.connectedAs', { username: status.x_username })
            : t('xBookmarksIntegration.success.subtitle')}
        </DialogDescription>
      </DialogHeader>

      <DialogScrollArea className="space-y-6 px-6">
      {status?.last_error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{status.last_error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bookmark className="w-4 h-4" />
            {t('xBookmarksIntegration.success.syncStatus')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={status?.connected ? 'default' : 'outline'}>
              {status?.connected
                ? t('xBookmarksIntegration.success.connected')
                : t('xBookmarksIntegration.success.notConnected')}
            </Badge>
            {status?.backfill_status && (
              <Badge variant={backfillBadgeVariant(status.backfill_status)}>
                {t(BACKFILL_LABEL_KEYS[status.backfill_status])}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {t('xBookmarksIntegration.success.importedCount', {
              count: status?.bookmarks_imported_count ?? 0,
            })}
          </p>
          {status?.last_sync_at && (
            <p className="text-sm text-muted-foreground">
              {t('xBookmarksIntegration.success.lastSync', {
                time: new Date(status.last_sync_at).toLocaleString(),
              })}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('xBookmarksIntegration.syncInterval.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {SYNC_INTERVALS.map((minutes) => (
            <Button
              key={minutes}
              type="button"
              size="sm"
              variant={syncInterval === minutes ? 'default' : 'outline'}
              onClick={() => handleIntervalChange(minutes)}
            >
              {t(SYNC_INTERVAL_LABEL_KEYS[minutes])}
            </Button>
          ))}
        </CardContent>
      </Card>

      </DialogScrollArea>
      <DialogFooter variant="scrollable" className="flex-wrap justify-between gap-3 px-6 pb-6">
        <Button variant="outline" onClick={onClose}>
          {t('xBookmarksIntegration.actions.done')}
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('xBookmarksIntegration.actions.refreshStatus')}
          </Button>
          <Button onClick={handleSyncNow} disabled={isSyncing || !status?.connected}>
            {isSyncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {t('xBookmarksIntegration.actions.syncNow')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDisconnect}
            disabled={isDisconnecting}
          >
            {isDisconnecting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            {t('xBookmarksIntegration.actions.disconnect')}
          </Button>
        </div>
      </DialogFooter>
    </div>
  );
};
